import { Link } from "react-router-dom";
import { ArrowLeft, Ticket, Sparkles, MessageCircle, Play, Flame } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { useCoreController } from "@/services/coreController";

type Game = {
  id: string;
  title: string;
  tagline: string;
  reward: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
  accent: string;
  badge?: string;
  to: string;
};

const GAMES: Game[] = [
  {
    id: "gratter",
    title: "Carte à Gratter",
    tagline: "Gratte · Dévoile · Gagne",
    reward: "Jusqu'à 100 000 FCFA",
    icon: Ticket,
    gradient: "linear-gradient(140deg,#ff6a6a 0%,#c8203a 55%,#4d0716 100%)",
    accent: "#ff6a6a",
    to: "/scratch-card",
  },
  {
    id: "roue",
    title: "Roue de la Chance",
    tagline: "Un tour, un bonus",
    reward: "Bonus jusqu'à 1 500 FCFA",
    icon: Sparkles,
    gradient: "linear-gradient(140deg,#a78bfa 0%,#6d5cf5 55%,#2a1d7a 100%)",
    accent: "#a78bfa",
    badge: "NEW",
    to: "/lottery",
  },
];

function JeuxPage() {
  const coreController = useCoreController();
  const whatsappUrl =
    coreController.appSettingResponse.whatsapp_support_link || "https://wa.me/22605756806";

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />
      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <Link to="/" aria-label="Retour" className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring">
            <ArrowLeft className="size-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-base font-semibold tracking-tight">Jeux EASYSERVICE EXPRESS</h1>
            <p className="text-[11px] text-muted-foreground">Tente ta chance après chaque dépôt</p>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 space-y-4 px-4 py-5 lg:max-w-6xl lg:px-8">
        <section className="flex items-center justify-between rounded-2xl bg-card-muted-2 px-4 py-3 ring-1 ring-card-muted-ring">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300/90">Tes essais</p>
            <p className="mt-0.5 text-sm font-semibold">1 essai disponible aujourd'hui</p>
          </div>
          <span className="grid size-10 place-items-center rounded-xl bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/30">
            <Flame className="size-5" />
          </span>
        </section>

        <section>
          <div className="mb-2.5 flex items-center justify-between px-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-subtle">Choisis ton jeu</p>
            <p className="text-[10px] text-text-subtle">{GAMES.length} jeux</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {GAMES.map((g) => (
              <Link
                key={g.id}
                to={g.to}
                className="group relative flex aspect-[3/4] flex-col overflow-hidden rounded-2xl text-left ring-1 ring-card-muted-ring transition active:scale-[0.98]"
                style={{ background: g.gradient }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{ background: "radial-gradient(120% 80% at 100% 0%, rgba(255,255,255,0.28), transparent 55%)" }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute -bottom-16 -left-10 size-40 rounded-full opacity-30 blur-2xl"
                  style={{ background: g.accent }}
                />

                {g.badge && (
                  <span className="absolute right-2.5 top-2.5 rounded-full bg-white/90 px-2 py-0.5 text-[9px] font-bold tracking-widest text-black backdrop-blur">
                    {g.badge}
                  </span>
                )}

                <div className="relative flex flex-1 items-center justify-center p-3">
                  <span className="grid size-16 place-items-center rounded-2xl bg-white/20 ring-1 ring-white/20 backdrop-blur">
                    <g.icon className="size-8 text-white drop-shadow" />
                  </span>
                </div>

                <div className="relative rounded-b-2xl bg-black/30 p-3 backdrop-blur-md">
                  <p className="text-[13px] font-semibold leading-tight text-white">{g.title}</p>
                  <p className="mt-0.5 text-[10px] text-white/75">{g.tagline}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-amber-300">{g.reward}</p>
                    <span className="grid size-6 place-items-center rounded-full bg-white text-black">
                      <Play className="size-3 fill-current" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
          Chaque dépôt de <b className="text-foreground">250 FCFA</b> minimum te donne <b className="text-foreground">1 essai</b> par jour sur le jeu de ton choix.
        </p>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-card-muted py-3 text-sm font-medium text-foreground ring-1 ring-card-muted-ring transition hover:bg-muted"
        >
          <MessageCircle className="size-4" /> Nous contacter
        </a>

        <p className="pt-1 text-center text-[10px] text-text-subtle">
          Jeu réservé aux clients EASYSERVICE EXPRESS. Voir conditions.
        </p>
      </main>

      <BottomNav />
    </div>
  );
}

export default JeuxPage;